import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { uploadToSiteMedia } from "@/lib/uploadAsset";
import { Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, Image as ImageIcon, X } from "lucide-react";

type Metric = { metric: string; label: string };
type CaseStudy = {
  id: string;
  slug: string;
  headline: string;
  summary: string | null;
  client_type: string | null;
  market: string | null;
  challenge: string | null;
  solution: string | null;
  results: string | null;
  metrics: Metric[];
  images: string[];
  video_url: string | null;
  featured: boolean;
  published: boolean;
  display_order: number;
  seo_title: string | null;
  meta_description: string | null;
};

const blank = (): CaseStudy => ({
  id: "",
  slug: "",
  headline: "",
  summary: "",
  client_type: "",
  market: "",
  challenge: "",
  solution: "",
  results: "",
  metrics: [],
  images: [],
  video_url: "",
  featured: false,
  published: false,
  display_order: 0,
  seo_title: "",
  meta_description: "",
});

const inp = { background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" } as React.CSSProperties;
const fg = { color: "hsl(0 0% 90%)" };
const mu = { color: "hsl(0 0% 55%)" };

const CaseStudiesManager = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CaseStudy | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("case_studies").select("*").order("display_order").order("created_at", { ascending: false });
    if (error) toast({ title: "Load failed", description: error.message, variant: "destructive" });
    setRows(((data || []) as any[]).map((r) => ({ ...r, metrics: r.metrics || [], images: r.images || [] })));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setDraft({ ...blank(), slug: `case-${Date.now()}` });
    setOpenId("__new__");
  };

  const startEdit = (r: CaseStudy) => {
    setDraft({ ...r });
    setOpenId(r.id);
  };

  const cancel = () => { setDraft(null); setOpenId(null); };

  const save = async () => {
    if (!draft) return;
    if (!draft.headline.trim() || !draft.slug.trim()) {
      toast({ title: "Headline and slug required", variant: "destructive" });
      return;
    }
    const payload = {
      slug: draft.slug.trim(),
      headline: draft.headline,
      summary: draft.summary,
      client_type: draft.client_type,
      market: draft.market,
      challenge: draft.challenge,
      solution: draft.solution,
      results: draft.results,
      metrics: draft.metrics,
      images: draft.images,
      video_url: draft.video_url || null,
      featured: draft.featured,
      published: draft.published,
      display_order: draft.display_order,
      seo_title: draft.seo_title,
      meta_description: draft.meta_description,
    };
    const op = openId === "__new__"
      ? supabase.from("case_studies").insert([payload])
      : supabase.from("case_studies").update(payload).eq("id", draft.id);
    const { error } = await op;
    if (error) { toast({ title: "Save failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Saved" });
    cancel();
    load();
  };

  const remove = async (r: CaseStudy) => {
    if (!confirm(`Delete "${r.headline}"?`)) return;
    await supabase.from("case_studies").delete().eq("id", r.id);
    load();
  };

  const onImageUpload = async (file: File) => {
    if (!draft) return;
    setUploading(true);
    try {
      const url = await uploadToSiteMedia(file, "case-studies");
      setDraft({ ...draft, images: [...draft.images, url] });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={fg}>Case Studies</h2>
          <p className="text-sm" style={mu}>Only published case studies appear on the public site.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={startNew} style={{ background: "hsl(207 68% 50%)", color: "hsl(0 0% 100%)" }}>
            <Plus size={14} className="mr-1" /> New
          </Button>
        </div>
      </div>

      {loading ? <p style={mu}>Loading…</p> : (
        <div className="space-y-2">
          {rows.length === 0 && openId !== "__new__" && (
            <p className="text-center py-8 text-sm" style={mu}>No case studies yet.</p>
          )}
          {openId === "__new__" && draft && <Editor draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} uploading={uploading} onUpload={onImageUpload} isNew />}
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
              <button onClick={() => openId === r.id ? cancel() : startEdit(r)} className="flex w-full items-center justify-between gap-3 p-4 text-left">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate" style={fg}>{r.headline || "(no headline)"}</span>
                    {r.published ? <span className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5" style={{ background: "hsl(140 50% 20%)", color: "hsl(140 60% 75%)" }}>Published</span> : <span className="text-[10px] uppercase rounded px-1.5 py-0.5" style={{ background: "hsl(0 0% 14%)", color: "hsl(0 0% 60%)" }}>Draft</span>}
                    {r.featured && <span className="text-[10px] uppercase rounded px-1.5 py-0.5" style={{ background: "hsl(45 80% 18%)", color: "hsl(45 80% 70%)" }}>Featured</span>}
                  </div>
                  <div className="mt-1 text-xs" style={mu}>{r.client_type || ", "} · {r.market || ", "} · {r.images.length} img · order {r.display_order}</div>
                </div>
                {openId === r.id ? <ChevronUp size={16} style={mu} /> : <ChevronDown size={16} style={mu} />}
              </button>
              {openId === r.id && draft && <Editor draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} onDelete={() => remove(r)} uploading={uploading} onUpload={onImageUpload} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function Editor({ draft, setDraft, onSave, onCancel, onDelete, isNew, uploading, onUpload }: {
  draft: CaseStudy; setDraft: (d: CaseStudy) => void;
  onSave: () => void; onCancel: () => void; onDelete?: () => void; isNew?: boolean;
  uploading: boolean; onUpload: (f: File) => void;
}) {
  return (
    <div className="border-t p-4 space-y-3" style={{ borderColor: "hsl(0 0% 14%)" }}>
      <div className="grid gap-3 md:grid-cols-2">
        <div><Label style={mu}>Headline</Label><Input value={draft.headline} onChange={(e) => setDraft({ ...draft, headline: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Slug</Label><Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Client type</Label><Input value={draft.client_type || ""} onChange={(e) => setDraft({ ...draft, client_type: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Market</Label><Input value={draft.market || ""} onChange={(e) => setDraft({ ...draft, market: e.target.value })} style={inp} /></div>
      </div>
      <div><Label style={mu}>Summary</Label><Textarea rows={2} value={draft.summary || ""} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} style={inp} /></div>
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label style={mu}>Challenge</Label><Textarea rows={4} value={draft.challenge || ""} onChange={(e) => setDraft({ ...draft, challenge: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Solution</Label><Textarea rows={4} value={draft.solution || ""} onChange={(e) => setDraft({ ...draft, solution: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Results</Label><Textarea rows={4} value={draft.results || ""} onChange={(e) => setDraft({ ...draft, results: e.target.value })} style={inp} /></div>
      </div>

      <div>
        <Label style={mu}>Metrics</Label>
        <div className="space-y-2">
          {draft.metrics.map((m, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="e.g. 3x" value={m.metric} onChange={(e) => { const a = [...draft.metrics]; a[i] = { ...a[i], metric: e.target.value }; setDraft({ ...draft, metrics: a }); }} style={inp} />
              <Input placeholder="e.g. pipeline lift" value={m.label} onChange={(e) => { const a = [...draft.metrics]; a[i] = { ...a[i], label: e.target.value }; setDraft({ ...draft, metrics: a }); }} style={inp} />
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, metrics: draft.metrics.filter((_, j) => j !== i) })}
                className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 70% 60%)", background: "transparent" }}><X size={14} /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, metrics: [...draft.metrics, { metric: "", label: "" }] })}
            className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
            <Plus size={12} className="mr-1" /> Add metric
          </Button>
        </div>
      </div>

      <div>
        <Label style={mu}>Images</Label>
        <div className="flex flex-wrap gap-2">
          {draft.images.map((url, i) => (
            <div key={i} className="relative">
              <img src={url} alt="" className="h-20 w-20 rounded-md object-cover" />
              <button onClick={() => setDraft({ ...draft, images: draft.images.filter((_, j) => j !== i) })}
                className="absolute -right-1 -top-1 rounded-full p-0.5" style={{ background: "hsl(0 70% 50%)" }}>
                <X size={12} color="white" />
              </button>
            </div>
          ))}
          <label className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-md border border-dashed" style={{ borderColor: "hsl(0 0% 25%)", color: "hsl(0 0% 50%)" }}>
            <ImageIcon size={20} />
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
          </label>
        </div>
        {uploading && <p className="mt-1 text-xs" style={{ color: "hsl(207 68% 60%)" }}>Uploading…</p>}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div><Label style={mu}>Video URL (optional)</Label><Input value={draft.video_url || ""} onChange={(e) => setDraft({ ...draft, video_url: e.target.value })} style={inp} placeholder="YouTube/Vimeo/MP4" /></div>
        <div><Label style={mu}>Display order</Label><Input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: Number(e.target.value) })} style={inp} /></div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div><Label style={mu}>SEO title</Label><Input value={draft.seo_title || ""} onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Meta description</Label><Input value={draft.meta_description || ""} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} style={inp} /></div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-4 text-sm" style={fg}>
          <label className="flex items-center gap-2"><input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} /> Published</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} /> Featured</label>
        </div>
        <div className="flex gap-2">
          {!isNew && onDelete && <Button size="sm" variant="outline" onClick={onDelete} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 70% 60%)", background: "transparent" }}><Trash2 size={14} className="mr-1" /> Delete</Button>}
          <Button size="sm" variant="outline" onClick={onCancel} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>Cancel</Button>
          <Button size="sm" onClick={onSave} style={{ background: "hsl(207 68% 50%)", color: "hsl(0 0% 100%)" }}>Save</Button>
        </div>
      </div>
    </div>
  );
}

export default CaseStudiesManager;
